// 27.11.25 14:10 AEDT - PWA Install Prompt Component for COOC
// Shows "Add to Home Screen" prompt for iOS and Android users

import React, { useState, useEffect } from 'react';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // 27.11.25 14:10 AEDT - Detect iOS devices
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // 27.11.25 14:10 AEDT - Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                      window.navigator.standalone === true;
    setIsStandalone(standalone);

    // 27.11.25 14:10 AEDT - Don't show prompt if already installed
    if (standalone) {
      console.log('27.11.25 14:10 AEDT - PWA already installed');
      return;
    }

    // 27.11.25 14:10 AEDT - Listen for beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      console.log('27.11.25 14:10 AEDT - Install prompt event captured');
      e.preventDefault();
      setDeferredPrompt(e);
      
      // 27.11.25 14:10 AEDT - Show prompt after 5 seconds
      setTimeout(() => {
        setShowInstallPrompt(true);
      }, 5000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // 27.11.25 14:10 AEDT - For iOS, show prompt after 5 seconds
    if (iOS && !standalone) {
      setTimeout(() => {
        setShowInstallPrompt(true);
      }, 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // 27.11.25 14:10 AEDT - Handle install button click (Android/Chrome)
  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      console.log('27.11.25 14:10 AEDT - No deferred prompt available');
      return;
    }

    console.log('27.11.25 14:10 AEDT - Showing install prompt');
    deferredPrompt.prompt();
    
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`27.11.25 14:10 AEDT - User choice: ${outcome}`);
    
    if (outcome === 'accepted') {
      console.log('27.11.25 14:10 AEDT - User accepted install');
    }
    
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  // 27.11.25 14:10 AEDT - Handle dismiss
  const handleDismiss = () => {
    console.log('27.11.25 14:10 AEDT - User dismissed install prompt');
    setShowInstallPrompt(false);
    
    // 27.11.25 14:10 AEDT - Don't show again for 7 days
    localStorage.setItem('cooc-pwa-dismissed', Date.now().toString());
  };

  // 27.11.25 14:10 AEDT - Don't show if dismissed recently
  useEffect(() => {
    const dismissed = localStorage.getItem('cooc-pwa-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      
      if (daysSinceDismissed < 7) {
        console.log('27.11.25 14:10 AEDT - Install prompt recently dismissed, hiding');
        setShowInstallPrompt(false);
      }
    }
  }, []);

  // 27.11.25 14:10 AEDT - Don't render if shouldn't show
  if (!showInstallPrompt || isStandalone) {
    return null;
  }

  return (
    <div style={styles.container}>
      <div style={styles.promptBox}>
        <button 
          onClick={handleDismiss}
          style={styles.closeButton}
          aria-label="Dismiss"
        >
          ✕
        </button>
        
        <div style={styles.icon}>
          <img 
            src="/icons/icon-96x96.png" 
            alt="COOC icon" 
            style={styles.iconImage}
          />
        </div>

        <div style={styles.content}>
          <h3 style={styles.title}>Install COOC App</h3>
          <p style={styles.description}>
            {isIOS 
              ? 'Add COOC to your home screen for easy access to your documents'
              : 'Install COOC on your device for faster access and offline support'}
          </p>

          {isIOS ? (
            <div style={styles.iosInstructions}>
              <p style={styles.iosStep}>
                1. Tap the <strong>Share</strong> button <span style={styles.iosIcon}>⎙</span>
              </p>
              <p style={styles.iosStep}>
                2. Scroll down and tap <strong>"Add to Home Screen"</strong>
              </p>
              <p style={styles.iosStep}>
                3. Tap <strong>"Add"</strong> in the top right
              </p>
            </div>
          ) : (
            <button 
              onClick={handleInstallClick}
              style={styles.installButton}
            >
              Install COOC
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// 27.11.25 14:10 AEDT - Styles for install prompt
const styles = {
  container: {
    position: 'fixed',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 9999,
    maxWidth: '400px',
    width: 'calc(100% - 40px)',
    animation: 'slideUp 0.3s ease-out'
  },
  promptBox: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    padding: '20px',
    position: 'relative',
    border: '1px solid #e0e0e0'
  },
  closeButton: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#666',
    padding: '5px',
    lineHeight: '1'
  },
  icon: {
    textAlign: 'center',
    marginBottom: '15px'
  },
  iconImage: {
    width: '64px',
    height: '64px',
    borderRadius: '12px'
  },
  content: {
    textAlign: 'center'
  },
  title: {
    margin: '0 0 10px 0',
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333'
  },
  description: {
    margin: '0 0 15px 0',
    fontSize: '14px',
    color: '#666',
    lineHeight: '1.5'
  },
  installButton: {
    backgroundColor: '#4A90E2',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    width: '100%',
    transition: 'background-color 0.2s'
  },
  iosInstructions: {
    textAlign: 'left',
    backgroundColor: '#f5f5f5',
    padding: '15px',
    borderRadius: '8px',
    marginTop: '10px'
  },
  iosStep: {
    margin: '8px 0',
    fontSize: '14px',
    color: '#333',
    lineHeight: '1.5'
  },
  iosIcon: {
    fontSize: '18px',
    verticalAlign: 'middle'
  }
};

export default PWAInstallPrompt;